<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from 'vue';
import { useBookmarks } from '@ilteducation/annotations';
import { storeToRefs } from 'pinia';
import useReaderStore from '../../stores/reader';
import useUiStore from '../../stores/ui';
import BookmarkNavigationItem from './BookmarkNavigationItem.vue';
import AddBookmarkDialog from './AddBookmarkDialog.vue';
import BookmarkFormDialog from './BookmarkFormDialog.vue';
import useLocale from '../../composables/useLocale';
import type { GoToItem } from '../../types';
import type { Bookmark } from '@ilteducation/annotations-types';
import useRenditionStore from '../../stores/rendition';

type Emits = {
  (e: 'goToBookmark', item: GoToItem): void;
};
const emit = defineEmits<Emits>();

type Props = {
  search: string;
  bookId: string;
  profileId?: string;
};
const props = defineProps<Props>();
const { bookId } = toRefs(props);

const { t } = useLocale();
const { bookmarks, load, update, remove } = useBookmarks(bookId);
const { isBookmarkBtnsControls, hasBookmarks } = storeToRefs(useReaderStore());
const { toggleAnnotationsDrawer } = useUiStore();
const { compareCFIs } = useRenditionStore();

// Dialog state
const showEditDialog = ref(false);
const editingBookmark = ref<Bookmark | null>(null);
const isSaving = ref(false);
const isRemoving = ref(false);
const workingId = ref<string | null>(null); // Track which highlight is being saved/deleted

onMounted(async () => {
  try {
    await load();
  } catch (error) {
    console.error('Error loading bookmarks:', error);
  }
});

const sortBookmarksByCFI = (bookmarkArray: Bookmark[]): Bookmark[] =>
  bookmarkArray.sort((a, b) => compareCFIs(a.position, b.position));

const filteredBookmarks = computed(() => {
  const query = props.search.toLowerCase().trim();
  if (!query) return bookmarks.value;

  const filtered = query
    ? bookmarks.value.filter((bookmark) => bookmark.name.toLowerCase().includes(query))
    : bookmarks.value;

  return sortBookmarksByCFI([...filtered]);
});

const handleEdit = (bookmark: Bookmark) => {
  editingBookmark.value = bookmark;
  showEditDialog.value = true;

  setTimeout(() => toggleAnnotationsDrawer(false), 100);
};

const handleGoToBookmark = (item: GoToItem) => {
  // Close annotations drawer when navigating
  setTimeout(() => toggleAnnotationsDrawer(false), 100);

  emit('goToBookmark', item);
};

const handleSave = async (name: string, comment: string) => {
  if (!editingBookmark.value) return;

  try {
    isSaving.value = true;
    await update(editingBookmark.value.id, {
      name,
      comment: comment ? { type: 'text', content: comment } : undefined,
    });
    showEditDialog.value = false;
    editingBookmark.value = null;
  } catch (err) {
    console.error('Failed to update bookmark:', err);
  } finally {
    isSaving.value = false;
  }
};

const handleRemove = async (bookmark?: Bookmark) => {
  const bookmarkToRemove = bookmark || editingBookmark.value;
  if (!bookmarkToRemove) return;

  try {
    workingId.value = bookmarkToRemove.id;
    isRemoving.value = true;
    await remove(bookmarkToRemove.id);

    // Close dialog if removing from dialog
    if (!bookmark) {
      showEditDialog.value = false;
      editingBookmark.value = null;
    }
  } catch (err) {
    console.error('Failed to delete bookmark:', err);
  } finally {
    isRemoving.value = false;
    workingId.value = null;
  }
};
</script>

<template>
  <v-sheet data-testid="bp-bookmarks-tab">
    <v-list v-if="filteredBookmarks.length">
      <BookmarkNavigationItem
        v-for="bookmark in filteredBookmarks"
        :key="bookmark.id"
        :bookmark="bookmark"
        :is-saving="workingId === bookmark.id"
        @go-to-bookmark="handleGoToBookmark"
        @edit="handleEdit"
        @remove="handleRemove"
      />
    </v-list>

    <p v-else class="text-center pa-4">
      {{ t('book_player.message_no_bookmarks_found') }}
    </p>

    <div class="pa-4 pt-0">
      <AddBookmarkDialog
        v-if="hasBookmarks && isBookmarkBtnsControls && bookId"
        btn-type="button"
        :book-id="bookId"
        :profile-id="profileId"
      />
    </div>

    <BookmarkFormDialog
      v-if="editingBookmark"
      v-model="showEditDialog"
      :bookmark="editingBookmark"
      :is-saving="isSaving"
      :is-removing="isRemoving"
      @save="handleSave"
      @remove="handleRemove()"
    />
  </v-sheet>
</template>
